import React from "react";
import { Link } from "react-router-dom";
import {
    UploadCloud, Swords, ShieldCheck, GitBranch, Bot, Lock, ArrowRight, FileCode2,
} from "lucide-react";
import { Panel } from "@/components/common/Panel";

const GROUPS = [
    {
        icon: UploadCloud, title: "Intake & Analysis", to: "/workspaces", cta: "Open Workspaces",
        items: [
            "Isolated offline workspaces per engagement — nothing ever leaves the boundary",
            "Upload ABAP source, SU24/SU22 proposals, OData EDMX — or paste raw text",
            "Authorization, SU24 and OData engines inspect every artifact statically",
            "Findings with severity, evidence, affected objects and rule references",
            "Knowledge Graph: artifacts → programs → auth objects → findings, on a 3D depth field",
            "Instant Analysis: any T-code or Fiori App ID resolved offline in one keystroke",
            "Timeline snapshot on every run — Compare shows posture movement over time",
        ],
    },
    {
        icon: Swords, title: "Role Engineering — Sentinel Forge", to: "/roles", cta: "Open Sentinel Forge",
        items: [
            "Mass-generate PFCG roles from pasted specifications",
            "30 data-driven templates across 23+ real SAP modules, plus P2P / O2C composites",
            "17,226 real Fiori apps searchable by module and S/4HANA release — fully offline",
            "Engineer roles straight from real Fiori Business Catalogs and reusable Fiori Groups",
            "Authorization Matrix Manager: org-level fields, ACTVT/field editor, simulate addition, release readiness",
            "One-click — or whole-library — migration of T-code roles onto Fiori catalogs with a coverage score",
            "Consolidation analysis to collapse redundant roles",
            "Byte-accurate .SAP reference export, including the Fiori launchpad menu hierarchy",
        ],
    },
    {
        icon: ShieldCheck, title: "SoD & Governance", to: "/governance", cta: "Open Governance Map",
        items: [
            "258 real SoD risks and 26 critical activities from a genuine SAP GRC Access Control export",
            "Within-role and cross-role simulation — catches toxic combinations only visible across roles",
            "Field/ACTVT-level verification that can confirm or clear a conflict on real values",
            "28-control Mitigation Library, suggested automatically wherever a violation is detected",
            "Compliance Map across 10 global frameworks (SOX, GDPR, JSOX and more)",
            "Ruleset Configuration with admin CRUD; every rule tagged GRC EXPORT or ENRICHED so provenance is never in doubt",
            "APO / CRM / SRM / Consolidation enrichment overlay — 74 risks the raw export left dormant now fire",
            "Change Impact Brief attached to every approval, and a weekly Posture Digest for admins",
            "SoD conflict heatmap and top-toxic-roles widget on the Executive Overview",
        ],
    },
    {
        icon: GitBranch, title: "Diagrams & Deliverables", to: "/reports", cta: "Open Reports",
        items: [
            "Diagram Studio: three-tier role model, SoD matrix, authorization concept, P2P and O2C flows",
            "Refine any diagram in plain English; every version kept in that role's diagram history",
            "Cross-role toxic-combination matrix straight from a simulation",
            "Vector SVG and high-resolution PNG export — text stays crisp and searchable",
            "BPD / PDD .docx generated per role, with the diagram embedded",
            "Workspace exports in PDF, DOCX, XLSX and PPTX; full role-catalog export",
            "Branded masked share links with QR codes, usage tracking and instant revocation",
        ],
    },
    {
        icon: FileCode2, title: "Custom Code & RICEFW Clearance", to: "/code-review", cta: "Open Code Review",
        items: [
            "Authorization-centric ABAP static analysis: missing AUTHORITY-CHECK, unchecked sy-subrc, dangerous SUBMIT and dynamic calls, hardcoded users, client-open selects",
            "Every finding severity-rated with the offending line, the risk it creates and a corrected code pattern",
            "Ingest your ATC error export (.xlsx) and fold it into the same clearance verdict",
            "RICEFW security clearance report as a downloadable PDF — pass, conditional or fail",
            "Optional AI refinement of any finding by the UTSAVA Reasoner",
        ],
    },
    {
        icon: Bot, title: "Utsav — the AI Agent", to: "/utsav", cta: "Talk to Utsav",
        items: [
            "Chat or speak — microphone transcription in, spoken replies out",
            "Pick UTSAVA Reasoner Deep for depth or Fast for speed",
            "Acts on the platform: look up T-codes, run SoD simulations, describe roles, draft BPD/PDD, generate images",
            "Attach files — ABAP source, ATC/SU24 spreadsheets, PDF, DOCX, CSV, screenshots — and he parses and reasons over them",
            "Take the work out: PDF or Markdown transcript, code blocks as .abap, any table as .xlsx, generated images",
            "Remembers facts about you and your projects across sessions",
            "Session sidebar — resume any earlier conversation",
        ],
    },
    {
        icon: Lock, title: "Platform & Security", to: "/settings", cta: "Open Settings",
        items: [
            "Zero live SAP connectivity — every engine reasons on artifacts you control",
            "Role-gated access: admin, architect, consultant, auditor",
            "Dynamic RBAC — admin-defined custom roles scoped to specific modules",
            "Human approval on every change; nothing auto-applies",
            "Every action written to an immutable audit trail",
            "Self-service registration with mobile and security-question recovery",
        ],
    },
];

/** Complete, grouped capability map of the platform — the "what can this do?"
 * answer for a first-time visitor or a client evaluation. */
export function CapabilityMap() {
    return (
        <Panel eyebrow="Capability Map" title="Everything UTSAVA Genesis does">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="about-capability-map">
                {GROUPS.map((g) => (
                    <div key={g.title} data-testid={`capability-group-${g.title.split(" ")[0].toLowerCase()}`} className="panel-elevated p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <g.icon size={15} className="text-cyan-400 shrink-0" />
                            <h3 className="font-display text-base text-white">{g.title}</h3>
                        </div>
                        <ul className="space-y-1.5">
                            {g.items.map((it) => (
                                <li key={it} className="flex items-start gap-2 text-[13px] text-slate-400 leading-relaxed">
                                    <span className="mt-[7px] w-1 h-1 rounded-full bg-cyan-400/70 shrink-0" />
                                    {it}
                                </li>
                            ))}
                        </ul>
                        <Link to={g.to} className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-cyan-400 hover:text-cyan-300 transition-colors">
                            {g.cta} <ArrowRight size={11} />
                        </Link>
                    </div>
                ))}
            </div>
        </Panel>
    );
}
